<script setup lang="ts">
  import type { RestConfig } from '@solfacil/plasma-utils';
  import { useApi, useKeycloak } from '@solfacil/plasma-utils';
  import { storeToRefs } from 'pinia';

  import UserApi from './apis/ecommerce/UserApi';
  import { CHANNEL } from './constants';
  import type {
    GetByPartnerIdOut,
    GetPartnersQueryVariables,
    Partner,
    User,
  } from './types/ecommerce/generated.codegen';
  import {
    useGetCartByPartnerIdLazyQuery,
    useGetPartnerLazyQuery,
    useGetPartnersLazyQuery,
    useGetScoreSolfacilPlusLazyQuery,
    useGetUserFeatureFlagsQuery,
    useGetUserTermsLazyQuery,
  } from './types/ecommerce/generated.codegen';
  const route = useRoute();

  const config = useRuntimeConfig().public;

  const userStore = useUserStore();

  const { inPersonification, user, isAdmin } = storeToRefs(userStore);

  const cart = ref<GetByPartnerIdOut>();
  const regionStore = useRegionStore();
  const featureFlagsStore = useFeatureFlagsStore();

  provide('cart', cart);

  const { updateCartSnapshot } = useUserJourney();
  watch(
    cart,
    newCart => {
      if (newCart) updateCartSnapshot(newCart);
    },
    { deep: true },
  );

  useAnalyticsIdentify();
  regionStore.setRegionInitialValue(
    user.value,
    Boolean(inPersonification.value),
  );
  const { initialize } = useHotjar();

  initialize();

  useHotjarIdentify();
  const {
    onResult: onSolfacilScoreResult,
    load: loadSolfacilScore,
    loading: loadingSolfacilScore,
  } = useGetScoreSolfacilPlusLazyQuery(null, {
    fetchPolicy: 'network-only',
    context: {
      headers: {
        'x-impersonate-id': userStore.personifiedUserId,
      },
    },
  });

  watch(inPersonification, async () => {
    const partnerId = inPersonification.value
      ? (user.value as Partner)?.id
      : (user.value as User)?.partner?.id || 0;

    await loadSolfacilScore(null, { partner_id: partnerId });

    // When personification is finished, user will be redirected to the home page
    if (!inPersonification.value) {
      clearUrlParams();
      location.href = '/';
      return;
    }

    await navigateTo(
      {
        query: {
          segmentation_id: route.query.segmentation_id,
        },
      },
      { external: true },
    );
  });

  const featureFlagsQueryOptions = computed(() => ({
    fetchPolicy: 'network-only' as const,
    context: {
      headers: {
        'x-impersonate-id': userStore.personifiedUserId,
      },
    },
  }));

  const {
    onResult: onGetFeatureFlagsResult,
    onError: onGetFeatureFlagsError,
    refetch: refetchFeatureFlags,
  } = useGetUserFeatureFlagsQuery(featureFlagsQueryOptions);

  onGetFeatureFlagsResult(result => {
    if (result.data.getUserFeatureFlags) {
      featureFlagsStore.setFeatureFlags(result.data.getUserFeatureFlags);
      return;
    }

    featureFlagsStore.finishFeatureFlagsLoading();
  });

  onGetFeatureFlagsError(() => {
    featureFlagsStore.finishFeatureFlagsLoading();
  });

  watch(
    () => userStore.personifiedUserId,
    (personifiedUserId, previousPersonifiedUserId) => {
      if (personifiedUserId === previousPersonifiedUserId) return;

      featureFlagsStore.resetFeatureFlags();
      const refetchResult = refetchFeatureFlags();
      refetchResult?.catch(() => undefined);
    },
  );

  if (!route.meta.public) {
    const {
      onResult: onGetCartResult,
      load: loadCart,
      refetch: refetchCart,
      onError: onGetCartError,
    } = useGetCartByPartnerIdLazyQuery(null, {
      fetchPolicy: 'network-only',
      context: {
        headers: {
          'x-impersonate-id': userStore.personifiedUserId,
        },
      },
    });

    const { onResult: onGetUserTermsResult, load: loadUserTerms } =
      useGetUserTermsLazyQuery(null, {
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'x-impersonate-id': userStore.personifiedUserId,
          },
        },
      });

    onGetUserTermsResult(result => {
      if (!result.data) {
        return;
      }
      const { can_accept, is_current_term_accepted } =
        result.data.getUserTerms.operating_agreement;
      if (is_current_term_accepted) return;

      const HOST_LOJA = window.location.href;

      if (!can_accept) {
        navigateTo(`${config.HOST_INTEGRATOR}term-blocked`, { external: true });
        return;
      }

      navigateTo(`${config.HOST_INTEGRATOR}term?redirect=${HOST_LOJA}`, {
        external: true,
      });
    });

    provide('refetchCartGlobal', refetchCart);

    onGetCartResult(result => {
      regionStore.setLoadingRegion(false);
      if (!result.data) {
        return;
      }

      cart.value = result.data.getCartByPartner as GetByPartnerIdOut;

      if (
        cart.value?.region &&
        (cart.value.products?.length || cart.value.items?.length)
      ) {
        const c = cart.value;
        regionStore.setDeliveryRegion({
          region: c.region ?? '',
          subregion: c.subregion ?? '',
          zipcode: c.zipcode ?? '',
        });
      }
    });

    onGetCartError(() => {
      regionStore.setLoadingRegion(false);
    });

    const setCart = (value: GetByPartnerIdOut) => {
      cart.value = value;
    };

    provide('setCart', setCart);

    onSolfacilScoreResult(result => {
      if (!result?.data?.getScoreSolfacilPlus) {
        return;
      }

      userStore.setScoreSolfacilPlus(result.data.getScoreSolfacilPlus);
    });

    await new UserApi(useApi('ecommerce' as RestConfig))
      .getUser()
      .then(result => {
        userStore.setUser(result);

        const adminPermission = userStore
          .getPermissions()
          .some(
            (group: string) =>
              group.includes('admin') || group.includes('full_access'),
          );

        if (
          useKeycloak().isAuthenticated() &&
          !adminPermission &&
          !inPersonification.value
        ) {
          loadUserTerms(null, { userId: result?.id });
        }

        const getCartFilter = {
          partner_id: userStore.partnerId || 0,
          channel: CHANNEL,
        };

        const partnerId = userStore.partnerId || 0;
        loadSolfacilScore(null, { partner_id: partnerId });
        loadCart(null, getCartFilter);
        // Isso vai adicionar um skeleton loading no componente de estado de entrega
        regionStore.setLoadingRegion(true);
      });
  }

  function clearCart() {
    cart.value = undefined;
  }
  provide('clearCartHeader', clearCart);

  const { track } = useTracker();
  // Solução para recarregar quando não encontrar arquivos JS
  window.addEventListener(
    'error',
    e => {
      // @ts-expect-error - e.target.src is not typed
      const srcError = e.target.src;
      if (srcError?.includes('/_nuxt/')) {
        track('error_500_nuxt_file_not_found', { srcError });
        location.reload();
      }
    },
    true,
  );

  const partnersList = ref<Partner[]>([]);

  const filterSearchPartner = ref<GetPartnersQueryVariables>({
    term: (route.query.partner_cnpj as string) || '',
    page: 0,
  });

  const { onResult: onSearchPartnersResult, load: fetchSearchPartners } =
    useGetPartnersLazyQuery(filterSearchPartner, {
      fetchPolicy: 'network-only',
    });

  const { onResult: onGetPartnerResult, load: fetchGetPartner } =
    useGetPartnerLazyQuery();

  onSearchPartnersResult(async result => {
    if (!result.data?.getPartners) {
      return;
    }
    partnersList.value = (result.data.getPartners.partners as Partner[]) || [];

    if (route.query.partner_cnpj) {
      const partner = partnersList.value.find(
        partner => partner.cnpj === route.query.partner_cnpj,
      );
      if (partner?.id) {
        fetchGetPartner(null, {
          partner_id: partner?.id,
        });
      } else {
        redirect();
      }
    } else {
      redirect();
    }
  });

  onGetPartnerResult(result => {
    if (!result.data?.getPartner) {
      return;
    }
    userStore.setPersonifiedUser(result.data.getPartner as Partner);
    redirect();
  });

  async function redirect() {
    const { partner_cnpj: _partnerCnpj, ...params } = await route.query;
    await navigateTo(
      {
        query: {
          ...params,
        },
      },
      { external: true },
    );
  }

  onMounted(() => {
    // Query partner_cnpj is now a trigger for personification
    if (!route.query.partner_cnpj) {
      return;
    }

    // Block access to non-admin users
    if (!isAdmin.value) {
      clearUrlParams();
      location.href = '/';
      return;
    }

    // Trigguer personification logic
    fetchSearchPartners();
  });

  // Esse watch é necessário para limpar o "carinho" que é criado em tela quando o usuário entra na loja vindo de um orçamento.
  watch(
    () => route.query,
    (newQuery, oldQuery) => {
      // Caso segmentation_id não exista mais significa que o orçamento não é mais necessário.
      if (oldQuery?.segmentation_id && !newQuery?.segmentation_id) {
        cart.value = undefined;
      }
    },
    { deep: true },
  );
</script>

<template>
  <div id="app-root" class="app-root">
    <NuxtLoadingIndicator
      class="app-route-loading"
      :height="3"
      color="#BFFF00"
      :throttle="150" />
    <div
      v-if="route.query.partner_cnpj || loadingSolfacilScore"
      class="page-loading personify-page-loading">
      <slot name="load-indicator">
        <PageLoader class="icon-loader" />
      </slot>
    </div>
    <NuxtLayout v-else>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>

<style lang="scss">
  .app-route-loading {
    z-index: 10000;
  }

  .page-loading {
    @apply flex justify-center items-center;
    @apply h-screen;
    @apply pointer-events-none;
    @apply z-50;
  }

  .personify-page-loading {
    .icon-size-large {
      height: 4rem !important;
      width: 4rem !important;
    }
  }
</style>
